import { DataContainer } from "@app/classes/infrastructure/datacontainer";
import { DataContainerService } from "@app/classes/infrastructure/datacontainer.service";
import { IPersistable } from "@app/classes/infrastructure/IPersistable";
import { TransportData } from "@app/classes/infrastructure/transportdata";
import { ValidationResultAccumulator } from "@app/classes/infrastructure/validationresultaccumulator";
import { IdProvider } from "@app/services/idprovider.service";
import { Utils } from "@app/services/utils.service";
import { isNullOrUndefined } from "src/tools";
import { HealthDepartmentProfileFetchRequest } from "./healthdepartmentprofilefetchrequest";
import { UIUtils } from "@app/services/uiutils.service";
import { RequestTypes } from "@app/classes/infrastructure/enums";
import { PayloadPacketFacade } from "@app/classes/infrastructure/payloadpacket/payloadpacketfacade";
import { ServerCommunicatorService } from "@app/services/server-communicator.service";

export class HealthDepartmentProfileProps {

  public created_date: string = '';
  public updated_date: string = '';
  public created_by: number = 0;
  public updated_by: number = 0;

  public id: number = 0;
  public name_en: string = '';
  public name_mr: string = '';
  public designation_en: string = '';
  public designation_mr: string = '';
  public seniority_level: number = 0;
  public image_blob_url: string = '';

  selected_photos: File[] = [];
  public profile_pic_path: string = '';

  public readonly is_newly_created: boolean = false;


  private constructor(IsNewlyCreated: boolean) {
    this.is_newly_created = IsNewlyCreated;
  }

  public static Blank() {
    return new HealthDepartmentProfileProps(true);
  }
}

export class HealthDepartmentProfile implements IPersistable<HealthDepartmentProfile> {
  public static readonly mastertablename: string = 'health_department_profile_master';

  private constructor(public readonly p: HealthDepartmentProfileProps, public readonly AllowEdit: boolean) {

  }

  public async EnsurePrimaryKeysWithValidValues(): Promise<void> {
    if (this.p.id === undefined || this.p.id === 0) {
      const newids = await IdProvider.GetInstance().GetNextEntityId();
      // const newids = await IdProvider.GetInstance().GetAllocateSingleIds();
      this.p.id = newids[0];
      if (this.p.id <= 0) throw new Error("Cannot assign Id. Please try again");
    }
  }

  public GetEditableVersion(): HealthDepartmentProfile {
    let newHealthDepartmentProfile: HealthDepartmentProfileProps = Utils.GetInstance().DeepCopy(this.p);
    return HealthDepartmentProfile.CreateInstance(newHealthDepartmentProfile, true);
  }

  public static CreateNewInstance() {
    return new HealthDepartmentProfile(HealthDepartmentProfileProps.Blank(), true);
  }

  public static CreateInstance(data: any, allowEdit: boolean) {
    return new HealthDepartmentProfile(data as HealthDepartmentProfileProps, allowEdit);
  }

  public CheckSaveValidity(_td: TransportData, vra: ValidationResultAccumulator): void {
    if (!this.AllowEdit) vra.add('', 'This object is not editable and hence cannot be saved.');
    //  if (!this.p.Designation || this.p.Designation.trim() == '') {
    //       vra.add('Designation', 'Designation cannot be blank. ');
    //     }
    //     if (!this.p.Designation || this.p.Designation.trim() == '') {
    //       vra.add('Designation', 'Designation cannot be blank. ');
    //     }
    // if (this.p.HealthDepartmentProfileRef <= 0) vra.add('HealthDepartmentProfileRef', 'HealthDepartmentProfile cannot be blank. ');
    // if (this.p.CityRef <= 0) vra.add('CityRef', 'City cannot be blank. ');
  }

  public MergeIntoTransportData(td: TransportData) {
    DataContainerService.GetInstance().MergeIntoContainer(td.MainData, HealthDepartmentProfile.mastertablename, this.p);
  }

  private static m_currentInstance: HealthDepartmentProfile = HealthDepartmentProfile.CreateNewInstance();

  public static GetCurrentInstance() {
    return HealthDepartmentProfile.m_currentInstance;
  }

  public static SetCurrentInstance(value: HealthDepartmentProfile) {
    HealthDepartmentProfile.m_currentInstance = value;
  }

  // ********************************************
  public static cacheDataChangeLevel: number = -1;

  public static SingleInstanceFromTransportData(td: TransportData): HealthDepartmentProfile {
    let dcs = DataContainerService.GetInstance();
    if (dcs.CollectionExists(td.MainData, HealthDepartmentProfile.mastertablename)) {
      for (let data of dcs.GetCollection(td.MainData, HealthDepartmentProfile.mastertablename)!.Entries) {
        return HealthDepartmentProfile.CreateInstance(data, false);
      }
    }

    return null as any;
  }

  public static ListFromDataContainer(cont: DataContainer,
    filterPredicate: (arg0: any) => boolean = null as any,
    sortPropertyName: string = "Name"): HealthDepartmentProfile[] {
    let result: HealthDepartmentProfile[] = [];

    let dcs = DataContainerService.GetInstance();

    if (dcs.CollectionExists(cont, HealthDepartmentProfile.mastertablename)) {
      let coll = dcs.GetCollection(cont, HealthDepartmentProfile.mastertablename)!;
      let entries = coll.Entries;

      if (!isNullOrUndefined(filterPredicate)) entries = entries.filter(filterPredicate);

      if (sortPropertyName.trim().length > 0) {
        entries = Utils.OrderByPropertyName(entries, sortPropertyName);
      }

      for (let data of entries) {
        result.push(HealthDepartmentProfile.CreateInstance(data, false));
      }
    }

    return result;
  }

  public static ListFromTransportData(td: TransportData): HealthDepartmentProfile[] {
    return HealthDepartmentProfile.ListFromDataContainer(td.MainData);
  }

  public static async FetchTransportData(req: HealthDepartmentProfileFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }

  public static async FetchTransportDataWithoutLoginToken(req: HealthDepartmentProfileFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequestWithoutLoginToken(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }

  // Function created For Trasaction Service filters 
  public static async FetchTransportDataForTransactionFilter(req: HealthDepartmentProfileFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }

  public static async FetchInstance(id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new HealthDepartmentProfileFetchRequest();
    req.health_department_profile_ids.push(id);

    let tdResponse = await HealthDepartmentProfile.FetchTransportData(req, errorHandler) as TransportData;
    return HealthDepartmentProfile.SingleInstanceFromTransportData(tdResponse);
  }

  public static async FetchList(req: HealthDepartmentProfileFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdResponse = await HealthDepartmentProfile.FetchTransportData(req, errorHandler) as TransportData;
    return HealthDepartmentProfile.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireList(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new HealthDepartmentProfileFetchRequest();
    let tdResponse = await HealthDepartmentProfile.FetchTransportData(req, errorHandler) as TransportData;
    return HealthDepartmentProfile.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListWithoutLoginToken(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new HealthDepartmentProfileFetchRequest();
    let tdResponse = await HealthDepartmentProfile.FetchTransportDataWithoutLoginToken(req, errorHandler) as TransportData;
    return HealthDepartmentProfile.ListFromTransportData(tdResponse);
  }


  public static async FetchEntireListByStateRef(state_id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new HealthDepartmentProfileFetchRequest();
    req.state_ids.push(state_id)
    let tdResponse = await HealthDepartmentProfile.FetchTransportData(req, errorHandler) as TransportData;
    return HealthDepartmentProfile.ListFromTransportData(tdResponse);
  }

  // get all asha for Trasaction filter service file 
  public static async FetchEntireListForTransactionFilter(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new HealthDepartmentProfileFetchRequest();
    let tdResponse = await HealthDepartmentProfile.FetchTransportDataForTransactionFilter(req, errorHandler) as TransportData;
    return HealthDepartmentProfile.ListFromTransportData(tdResponse);
  }


  public async DeleteInstance(successHandler: () => Promise<void> = null!, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = new TransportData();
    tdRequest.RequestType = RequestTypes.Deletion;

    this.MergeIntoTransportData(tdRequest);
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
    }
    else {
      if (!isNullOrUndefined(successHandler)) await successHandler();
    }
  }

}
